import {
  PlanFeatures,
  FeatureCheckResult,
  EditableStoryCheck,
  EditableStoryInfo,
  ISubscriptionPlanService
} from './types';
import { PLAN_CONFIGURATION, normalizePlan, UNLIMITED } from './planConfiguration';

export class SubscriptionPlanService implements ISubscriptionPlanService {
  constructor() {}

  /** Get the feature limits and flags for a specific plan */
  public getFeatureLimits(plan: string): PlanFeatures {
    const key = normalizePlan(plan);
    return PLAN_CONFIGURATION[key];
  }

  /** Get the entire plan configuration */
  public getPlanConfiguration() {
    return PLAN_CONFIGURATION;
  }

  // ─── Limit checks ─────────────────────────────────────────────────────────

  public canCreateStory(plan: string, currentStoryCount: number): FeatureCheckResult {
    const features = this.getFeatureLimits(plan);
    if (features.maxStories === UNLIMITED) {
      return { allowed: true };
    }
    if (currentStoryCount >= features.maxStories) {
      return {
        allowed: false,
        reason: `Story limit reached. Your plan allows ${features.maxStories} stories.`,
        currentLimit: features.maxStories
      };
    }
    return { allowed: true, currentLimit: features.maxStories };
  }

  public canPublishStory(plan: string, currentPublishedCount: number): FeatureCheckResult {
    const features = this.getFeatureLimits(plan);
    if (features.maxPublishedStories === UNLIMITED) {
      return { allowed: true };
    }
    if (currentPublishedCount >= features.maxPublishedStories) {
      return {
        allowed: false,
        reason: `Published story limit reached. Your plan allows ${features.maxPublishedStories} published stories.`,
        currentLimit: features.maxPublishedStories
      };
    }
    return { allowed: true, currentLimit: features.maxPublishedStories };
  }

  public canCreateEditableStory(plan: string, currentEditableStories: EditableStoryInfo[]): EditableStoryCheck {
    const features = this.getFeatureLimits(plan);

    if (features.maxStories === UNLIMITED) {
      return { allowed: true, wouldFreezeStories: false };
    }

    const editableStories = currentEditableStories.filter(
      s => s.status !== 'archived' && s.status !== 'locked' && s.status !== 'template'
    );
    const currentEditableCount = editableStories.length;

    if (currentEditableCount < features.maxStories) {
      return { allowed: true, wouldFreezeStories: false, currentLimit: features.maxStories };
    }

    const sortedStories = [...editableStories].sort(
      (a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
    );
    const storiesImpacted = sortedStories.slice(features.maxStories - 1);

    return {
      allowed: true,
      wouldFreezeStories: true,
      storiesImpacted: storiesImpacted.map(s => ({
        id: s.id,
        name: s.name,
        lastUpdated: new Date(s.lastUpdated)
      })),
      message: `Creating a new story will lock ${storiesImpacted[0]?.name || 'an older story'}.`,
      currentLimit: features.maxStories
    };
  }

  public getEditableStories(plan: string, allStories: EditableStoryInfo[]): string[] {
    const features = this.getFeatureLimits(plan);
    const filtered = allStories.filter(s => s.status !== 'archived' && s.status !== 'locked');

    if (features.maxStories === UNLIMITED) {
      return filtered.map(s => s.id);
    }

    return filtered
      .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      .slice(0, features.maxStories)
      .map(s => s.id);
  }

  public isStoryEditable(plan: string, storyId: string, allStories: EditableStoryInfo[]): boolean {
    const editableIds = this.getEditableStories(plan, allStories);
    return editableIds.includes(storyId);
  }

  // ─── Feature flags (from planConfiguration) ───────────────────────────────

  public hasVersionHistory(plan: string): boolean {
    return this.getFeatureLimits(plan).versionHistoryEnabled;
  }

  public getMaxFileUploadSize(plan: string): number {
    return this.getFeatureLimits(plan).maxFileUploadSizeMB;
  }

  public canOverrideEnvPrefsToViewTemplates(plan: string): boolean {
    return this.getFeatureLimits(plan).canOverrideEnvPrefsToViewTemplates;
  }

  public canCreateTemplates(plan: string): boolean {
    return this.getFeatureLimits(plan).canCreateTemplates;
  }

  public canCreateTemplateScenes(plan: string, orgMember: { trackOneMember?: boolean } | null): boolean {
    return this.canCreateTemplates(plan) || (orgMember?.trackOneMember === true);
  }

  public canEditTemplatesDirectly(plan: string): boolean {
    return this.getFeatureLimits(plan).canEditTemplatesDirectly;
  }

  public canEditTemplateMetadata(plan: string): boolean {
    return this.getFeatureLimits(plan).canEditTemplateMetadata;
  }

  public canCopyProject(plan: string): boolean {
    return this.getFeatureLimits(plan).canCopyProject;
  }

  public canEditPendingLiveScenes(plan: string): boolean {
    return this.getFeatureLimits(plan).canEditPendingLiveScenes;
  }

  public canEditTemplateProjectName(plan: string): boolean {
    return this.getFeatureLimits(plan).canEditTemplateProjectName;
  }

  public canNavigateFreelyFromTemplate(plan: string): boolean {
    return this.getFeatureLimits(plan).canNavigateFreelyFromTemplate;
  }

  public canAccessVideoLibrary(plan: string, orgMember: { trackOneMember?: boolean } | null): boolean {
    return this.getFeatureLimits(plan).canAccessVideoLibrary || (orgMember?.trackOneMember === true);
  }

  public canGenerateVideo(plan: string): boolean {
    return this.getFeatureLimits(plan).canGenerateVideo;
  }

  public canAccessPrimitives3DLibrary(plan: string, orgMember: { trackOneMember?: boolean } | null): boolean {
    return this.getFeatureLimits(plan).canAccessPrimitives3DLibrary || (orgMember?.trackOneMember === true);
  }

  public canAccessPrimitives3DShapes(plan: string, orgMember: { trackOneMember?: boolean } | null): boolean {
    return this.getFeatureLimits(plan).canAccessPrimitives3DShapes || (orgMember?.trackOneMember === true);
  }

  public canUseApplessPlatform(plan: string): boolean {
    return this.getFeatureLimits(plan).canUseApplessPlatform;
  }
}
